import { useEffect, useState } from 'react'
import { getMyCdirInvoiceDetails, getMyCdirInvoicesByTenantId, getWalletInvoiceDetails } from '../../services/Invoice';
import Cookies from 'js-cookie';
import Pagination from '../../components/Pagination';
import jsPDF from 'jspdf';
import moment from 'moment';
import Cross from '../../icons/Cross';
import PleaseWaitPopup from '../../components/PleaseWaitPopup';
import useGroup from '../../hook/useGroup';
import { formatDateForDisplay } from '../../utils/getUserDateFormat';
import { INVOICE_TYPES, InvoiceType } from './Billing';

const heading = ["#", "Issued On", "Total Amount", "Status"];

const formatBillingCycle = (value?: string | null) => {
    if (!value) return "";

    const trimmed = value.trim();
    if (!trimmed) return "";

    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

type InvoiceProps = {
    isRefresh: boolean;
    setIsRefresh: (value: boolean) => void;
}

const Invoice = ({
    isRefresh,
    setIsRefresh
}: InvoiceProps) => {
    const { selectedTenantId, isSuperAdmin }: any = useGroup();
    const tenantId = isSuperAdmin
        ? selectedTenantId
        : Cookies.get("tenantId-my-cdir");
    const [creditCardData, setCreditCardData] = useState<any[]>([]);
    const [paginationModel, setPaginationModel] = useState<{ page: number, size: number }>({
        page: 0,
        size: 10
    });
    const [totalLength, setTotalLength] = useState<number>(0);
    const [showEntries, setShowEntries] = useState<number>(10);
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [openPdfDialog, setOpenPdfDialog] = useState<boolean>(false);
    const [pleaseWaitPopup, setPleaseWaitPopup] = useState<boolean>(false);
    const [loading, setLoading] = useState(false);
    const [refreshCount, setRefreshCount] = useState(0);

    useEffect(() => {
        if (isRefresh) {
            setPaginationModel({
                ...paginationModel,
                page: 0
            });

            setRefreshCount((prev) => prev + 1);
            setIsRefresh(false);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRefresh])

    useEffect(() => {
        if (tenantId) {
            setLoading(true);
            getMyCdirInvoicesByTenantId(tenantId, paginationModel.page, paginationModel.size)
                .then(res => {
                    setTotalLength(res?.count);

                    const data = res?.mycdirInvoice?.map((item: any) => ({
                        ...item,
                        invoiceDate: item?.invoiceDate
                            ? formatDateForDisplay(item?.invoiceDate)
                            : "",
                        totalAmount:
                            (item?.amountDue ?? 0) +
                            (item?.amountPaid ?? 0),
                    }));

                    setCreditCardData(data);
                })
                .catch(error => {
                    console.error(error);
                })
                .finally(() => {
                    setLoading(false);
                    setIsRefresh(false);
                });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenantId, paginationModel, refreshCount])

    const handlePreviewPdf = async (item: any) => {
        try {
            setPleaseWaitPopup(true);

            if (item?.invoiceType === INVOICE_TYPES.Subscription) {
                const res = await getMyCdirInvoiceDetails(item?.id);
                generateInvoicePdf(res, INVOICE_TYPES.Subscription);
            } else {
                const res = await getWalletInvoiceDetails(item?.id);
                generateInvoicePdf(res, INVOICE_TYPES.WalletRecharge);
            }

        } catch (error) {
            console.error(error);
            setPleaseWaitPopup(false);
        }
    }

    const generateInvoicePdf = async (data: any, invoiceType: InvoiceType) => {
        const doc = new jsPDF();

        let y = 15;

        const isWalletRecharge = invoiceType === INVOICE_TYPES.WalletRecharge;

        doc.setFillColor(41, 128, 185);
        doc.rect(0, 0, 210, 32, "F");

        doc.addImage(
            window.location.origin + "/images/datarepo-logo.png",
            "PNG",
            10,
            5,
            38,
            18
        );

        doc.setTextColor(255, 255, 255);

        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text("DataRepo", 60, 14);

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");

        doc.text(
            isWalletRecharge
                ? "MyCdir - Wallet Recharge Invoice"
                : "MyCdir - Subscription Invoice",
            60,
            22
        );

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("INVOICE", 190, 16, { align: "right" });

        y = 42;

        doc.setTextColor(0, 0, 0);
        doc.setFillColor(245, 247, 250);

        doc.roundedRect(
            10,
            y - 5,
            190,
            22,
            3,
            3,
            "F"
        );

        doc.setFontSize(11);
        doc.setFont("helvetica", "normal");

        doc.text(
            `Invoice #: ${data?.invoiceNumber || ""}`,
            15,
            y + 5
        );

        doc.text(
            `Date: ${data?.invoiceDate
                ? moment(data.invoiceDate).format("MM/DD/YYYY")
                : ""
            }`,
            120,
            y + 5
        );

        doc.text(
            `Status: ${data?.status || ""}`,
            15,
            y + 13
        );

        y += 30;

        const section = (title: string) => {
            doc.setFillColor(52, 152, 219);
            doc.rect(10, y - 5, 190, 8, "F");

            doc.setTextColor(255, 255, 255);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);

            doc.text(title, 12, y);

            doc.setTextColor(0, 0, 0);

            y += 12;
        }

        section("Customer Details");

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);

        doc.text(
            `Name: ${data?.customerName || ""}`,
            10,
            y
        );

        doc.text(
            `Email: ${data?.emailAddress || ""}`,
            120,
            y
        );

        y += 15;

        if (isWalletRecharge) {
            section("Wallet Details");

            doc.setFont("helvetica", "normal");

            doc.text(
                `Currency: ${data?.currency || "USD"}`,
                10,
                y
            );

            y += 15;

            section("Recharge Details");

            const amount = Number(data?.amount || 0);
            const balanceBefore = Number(data?.balanceBefore || 0);
            const balanceAfter = Number(data?.balanceAfter || 0);

            doc.setFillColor(236, 240, 241);

            doc.roundedRect(
                10,
                y - 5,
                190,
                55,
                3,
                3,
                "F"
            );

            doc.setFont("helvetica", "normal");
            doc.setTextColor(0, 0, 0);

            doc.text(
                "Recharge Amount",
                15,
                y + 5
            );

            doc.text(
                `${data?.currency || "USD"} ${amount.toFixed(2)}`,
                190,
                y + 5,
                { align: "right" }
            );

            doc.text(
                "Balance Before",
                15,
                y + 15
            );

            doc.text(
                `${data?.currency || "USD"} ${balanceBefore.toFixed(2)}`,
                190,
                y + 15,
                { align: "right" }
            );

            doc.setFont("helvetica", "bold");
            doc.setTextColor(39, 174, 96);

            doc.text(
                "New Wallet Balance",
                15,
                y + 27
            );

            doc.text(
                `${data?.currency || "USD"} ${balanceAfter.toFixed(2)}`,
                190,
                y + 27,
                { align: "right" }
            );

            doc.setTextColor(0, 0, 0);

            doc.setFont("helvetica", "normal");

            doc.text(
                "Transaction Type",
                15,
                y + 39
            );

            doc.text(
                data?.transactionType || "Wallet Recharge",
                190,
                y + 39,
                { align: "right" }
            );

            y += 65;

            section("Payment Information");

            doc.setFont("helvetica", "normal");

            doc.text(
                `Payment Method: ${data?.paymentType || "N/A"}`,
                10,
                y
            );

            doc.text(
                `Transaction ID: ${data?.transactionId || "N/A"}`,
                10,
                y + 8
            );

            y += 25;

            doc.setFillColor(232, 245, 233);

            doc.roundedRect(
                10,
                y - 5,
                190,
                28,
                3,
                3,
                "F"
            );

            doc.setFont("helvetica", "bold");
            doc.setTextColor(39, 174, 96);

            doc.text(
                "Recharge Completed",
                15,
                y + 7
            );

            doc.text(
                `${data?.currency || "USD"} ${amount.toFixed(2)}`,
                190,
                y + 7,
                { align: "right" }
            );

            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);

            doc.text(
                "The amount has been successfully added to your MyCdir wallet.",
                15,
                y + 16
            );

        } else {
            section("Subscription Details");

            doc.setFont("helvetica", "normal");

            doc.text(
                `Plan: ${data?.planName || ""}`,
                10,
                y
            );

            doc.text(
                `Billing: ${formatBillingCycle(data?.billingCycle)}`,
                120,
                y
            );

            y += 15;

            const planPrice = Number(data?.planPrice || 0);
            const discount = Number(data?.discount || 0);
            const totalPaid = Number(data?.finalAmount || 0);
            const outstanding = Number(data?.balance || 0);

            const taxes = data?.taxes?.length
                ? data.taxes
                : [];

            const baseHeight = 28;
            const taxHeight = taxes.length * 6;
            const extra = 18;

            const boxHeight =
                baseHeight +
                taxHeight +
                extra;

            doc.setFillColor(236, 240, 241);

            doc.roundedRect(
                10,
                y - 5,
                190,
                boxHeight,
                3,
                3,
                "F"
            );

            doc.setFont("helvetica", "normal");
            doc.setTextColor(0, 0, 0);

            doc.text(
                "Plan Price",
                15,
                y + 5
            );

            doc.text(
                `$${planPrice.toFixed(2)}`,
                190,
                y + 5,
                { align: "right" }
            );

            doc.text(
                "Discount",
                15,
                y + 12
            );

            doc.text(
                `$${discount.toFixed(2)}`,
                190,
                y + 12,
                { align: "right" }
            );

            let taxY = y + 19;

            taxes.forEach((tax: any) => {

                doc.text(
                    `${tax.taxName} (${tax.taxRate}%)`,
                    15,
                    taxY
                );

                doc.text(
                    `$${Number(tax.taxAmount || 0).toFixed(2)}`,
                    190,
                    taxY,
                    { align: "right" }
                );

                taxY += 6;
            });

            doc.setFont("helvetica", "bold");
            doc.setTextColor(39, 174, 96);

            doc.text(
                "Total Paid",
                15,
                taxY + 2
            );

            doc.text(
                `$${totalPaid.toFixed(2)}`,
                190,
                taxY + 2,
                { align: "right" }
            );

            doc.setTextColor(231, 76, 60);

            doc.text(
                "Outstanding",
                15,
                taxY + 10
            );

            doc.text(
                `$${outstanding.toFixed(2)}`,
                190,
                taxY + 10,
                { align: "right" }
            );

            doc.setTextColor(0, 0, 0);

            y += boxHeight + 10;

            section("Payment Info");

            doc.setFont("helvetica", "normal");

            doc.text(
                `Method: ${data?.paymentType || "N/A"}`,
                10,
                y
            );

            doc.text(
                `Transaction ID: ${data?.transactionId || "N/A"}`,
                10,
                y + 8
            );
        }

        doc.setFontSize(9);
        doc.setTextColor(120);

        doc.text(
            "This invoice is generated by DataRepo system (MyCdir product).",
            105,
            285,
            { align: "center" }
        );

        const blob = doc.output("blob");
        const url = URL.createObjectURL(blob);

        setPdfUrl(url);
        setOpenPdfDialog(true);
        setPleaseWaitPopup(false);
    }

    useEffect(() => {
        return () => {
            if (pdfUrl) URL.revokeObjectURL(pdfUrl);
        }
    }, [pdfUrl]);

    const handleClose = () => {
        setOpenPdfDialog(false);
        if (pdfUrl) URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
    }

    return (
        <div className="w-full border border-secondary rounded-md bg-primary">
            <h4 className="text-primary font-medium bg-secondary px-4 py-2 w-full">Invoices</h4>

            <div className="flex flex-col p-4 gap-y-3">

                <table>
                    <thead className="border-b-2 border-secondary">
                        <tr className="text-sm text-start">
                            {heading.map((item, index) => (
                                <th key={index} className="border-r border-secondary text-start px-0.5">
                                    {item}
                                </th>
                            ))}
                            <th className="border-r border-secondary text-start px-0.5 !pl-1.5">Actions</th>
                        </tr>
                    </thead>

                    <tbody className="border-b border-secondary">
                        {loading ? (
                            <tr>
                                <td
                                    colSpan={7}
                                    className="py-12"
                                >
                                    <div className="flex flex-col items-center justify-center gap-2">
                                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-[#2B3998]" />
                                        <span className="text-sm text-gray-500">
                                            Loading...
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            creditCardData?.map((item: any, index: number) => (
                                <tr
                                    key={index}
                                    className="text-sm text-start"
                                >
                                    <td className="border-r border-b border-secondary text-start px-0.5 py-1.5">{item?.id}</td>
                                    <td className="border-r border-b border-secondary text-start px-0.5 py-1.5">{item?.invoiceDate}</td>
                                    <td className="border-r border-b border-secondary text-end px-0.5 py-1.5">${item?.totalAmount === null ? "0.00" : (parseFloat(item?.totalAmount))?.toFixed(2)}</td>
                                    <td className="border-r border-b border-secondary text-start px-1.5 py-1.5">
                                        <span
                                            style={{
                                                backgroundColor: "#4ade80"
                                            }}
                                            className="px-1.5 py-[1px] w-fit rounded text-white"
                                        >
                                            {item?.status}
                                        </span>
                                    </td>
                                    <td className={`border-r border-b border-secondary text-center text-sm p-0.5`}>
                                        <span className="flex justify-center items-center text-[0.875rem] text-[#3b82f6]">
                                            <button
                                                title="Preview Invoice PDF"
                                                className="hover:opacity-80 transition"
                                                onClick={() => handlePreviewPdf(item)}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="20"
                                                    height="20"
                                                    viewBox="0 0 24 24"
                                                    fill="currentColor"
                                                >
                                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zm0 2.5L18.5 9H14zM8 13h8v2H8zm0 4h8v2H8zM8 9h5v2H8z" />
                                                </svg>
                                            </button>
                                        </span>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>

                <Pagination
                    totalLength={totalLength}
                    paginationModel={paginationModel}
                    setPaginationModel={setPaginationModel}
                    showEntries={showEntries}
                    setShowEntries={setShowEntries}
                />

            </div>

            {
                openPdfDialog && (
                    <div className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/50 p-4">

                        <div className="relative flex h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">

                            <div className="flex items-center justify-between border-b px-4 py-3">

                                <span className="text-base font-semibold">
                                    Invoice Preview
                                </span>

                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="rounded-md p-1 transition hover:bg-gray-100"
                                >
                                    <Cross />
                                </button>
                            </div>

                            {pdfUrl && (
                                <iframe
                                    id="pdf-frame"
                                    src={pdfUrl}
                                    className="h-full w-full"
                                    title="Invoice Preview"
                                />
                            )}
                        </div>
                    </div>
                )
            }

            <PleaseWaitPopup pleaseWaitPopup={pleaseWaitPopup} />
        </div>
    )
}

export default Invoice
